import { SingleFileViewerUploadEmptyState } from './ components/SingleFileViewerUploadEmptyState'
import { SingleFileViewerUploadStyled } from './styles/SingleFileViewerUploadStyled'
import { File } from '../File'
import {
  FileViewer,
  FileViewerComponents as FileViewerComponents,
} from '../FileViewer'
import { useFileViewerContext } from '../FileViewer/FileViewerContext'
import { type IUploadProps, type InitialUploadFile, Upload } from '../Upload'
import { type UploadFile } from '../Upload/types'
import { useUploadContext } from '../Upload/UploadContext'
import { IconButton } from '@ui/elements/Button'
import { IconDelete } from '@ui/elements/Icon'
import { Space } from '@ui/elements/Space'

export interface ISingleFileViewerUploadProps
  extends Omit<IUploadProps, 'initialFiles' | 'onBatchComplete'> {
  initialFile?: InitialUploadFile
  onUploadComplete?: (file: UploadFile) => void
}

const defaultStrings = {
  uploadEmptyStateText: 'Drag and drop your file here',
}

const ControlsSection = () => {
  const { selectedFile, getFileData } = useFileViewerContext<UploadFile>()
  const { commonProps } = useUploadContext()

  const { deleteFile, isDisabled } = commonProps

  const fileData = getFileData(selectedFile)

  return selectedFile ? (
    <FileViewerComponents.ControlsSection>
      <Space direction='horizontal' justify='space-between' align='center'>
        <File data={fileData} border={false} />

        {!isDisabled ? (
          <IconButton
            iconComponent={<IconDelete />}
            aria-label={`Delete ${fileData.name || fileData.file}`}
            onClick={() => deleteFile(selectedFile)}
          />
        ) : null}
      </Space>
    </FileViewerComponents.ControlsSection>
  ) : null
}

const MainContainer = () => {
  const { commonProps } = useUploadContext()

  const { files } = commonProps

  return files.length > 0 ? (
    <FileViewer<UploadFile>
      components={{ ControlsSection }}
      files={[files[0]]}
      selectedFile={files[0]}
      getFileData={(file) => {
        return {
          id: file.id,
          file: file.file,
          mimetype: file.mimetype,
          name: file.name,
          length: file.length,
        }
      }}
    />
  ) : (
    <SingleFileViewerUploadEmptyState />
  )
}

export const SingleFileViewerUpload = ({
  components,
  initialFile,
  strings,
  onUploadComplete,
  ...props
}: ISingleFileViewerUploadProps) => {
  return (
    <SingleFileViewerUploadStyled>
      <Upload
        replaceFilelist
        multiple={false}
        components={{
          MainContainer,
          ...components,
        }}
        initialFiles={initialFile ? [initialFile] : []}
        onBatchComplete={(files) => {
          if (onUploadComplete) {
            onUploadComplete(files[0])
          }
        }}
        strings={{
          ...defaultStrings,
          ...strings,
        }}
        {...props}
      />
    </SingleFileViewerUploadStyled>
  )
}
